import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import * as api from '../services/api';
import { PARTS, CATEGORIES, BASE_PRICE } from '../data/parts';
import { ForgeLegendModal } from '../components/legend';
import styles from './UploadPage.module.css';

export default function UploadPage() {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { isLoggedIn } = useAuth();
    const { config: currentConfig, exportConfig } = useConfig();
    const fileInputRef = useRef(null);
    const videoInputRef = useRef(null);

    const [step, setStep] = useState(1); // 1: Images, 2: Config, 3: Details
    const [images, setImages] = useState([]);
    const [video, setVideo] = useState(null);
    const [videoProgress, setVideoProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState({
        color: 'm04',
        wheels: 'standard_wheels',
        exhaust: 'stock_exhaust',
        seat: 'solo_seat',
        accessories: [],
    });
    const [formData, setFormData] = useState({
        title: '',
        description: '',
    });
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // 铸造传奇相关状态
    const [showForgeLegend, setShowForgeLegend] = useState(false);
    const [uploadedConfigId, setUploadedConfigId] = useState(null);
    const [uploadedCoverImage, setUploadedCoverImage] = useState(null);

    // Calculate total price
    const totalPrice = (() => {
        let total = BASE_PRICE;
        Object.entries(selectedConfig).forEach(([category, value]) => {
            if (Array.isArray(value)) {
                value.forEach(itemId => {
                    const part = PARTS[category]?.find(p => p.id === itemId);
                    if (part) total += part.price;
                });
            } else {
                const part = PARTS[category]?.find(p => p.id === value);
                if (part) total += part.price;
            }
        });
        return total;
    })();

    if (!isLoggedIn) {
        return (
            <div className={styles.notLoggedIn}>
                <h2>{t('upload.loginRequired') || 'Login Required'}</h2>
                <p>{t('upload.loginMessage') || 'Please login to upload your configuration.'}</p>
            </div>
        );
    }

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        const maxImages = 10;
        const remainingSlots = maxImages - images.length;

        if (files.length > remainingSlots) {
            setError(`Maximum ${maxImages} images allowed. You can add ${remainingSlots} more.`);
            return;
        }

        // Validate files
        const validFiles = files.filter(file => {
            if (!file.type.startsWith('image/')) {
                setError('Only image files are allowed');
                return false;
            }
            if (file.size > 10 * 1024 * 1024) {
                setError('Each image must be less than 10MB');
                return false;
            }
            return true;
        });

        // Create preview URLs
        const newImages = validFiles.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            id: Math.random().toString(36).substr(2, 9),
        }));

        setImages(prev => [...prev, ...newImages]);
        setError('');
    };

    const removeImage = (id) => {
        setImages(prev => {
            const img = prev.find(i => i.id === id);
            if (img) URL.revokeObjectURL(img.preview);
            return prev.filter(i => i.id !== id);
        });
    };

    const moveImage = (id, direction) => {
        setImages(prev => {
            const index = prev.findIndex(i => i.id === id);
            if (
                (direction === -1 && index === 0) ||
                (direction === 1 && index === prev.length - 1)
            ) {
                return prev;
            }
            const newImages = [...prev];
            [newImages[index], newImages[index + direction]] =
                [newImages[index + direction], newImages[index]];
            return newImages;
        });
    };

    const handleVideoSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate video file
        if (!file.type.startsWith('video/')) {
            setError(t('upload.errors.invalidVideo') || 'Please select a video file');
            return;
        }

        // Max 500MB
        if (file.size > 500 * 1024 * 1024) {
            setError(t('upload.errors.videoTooLarge') || 'Video must be less than 500MB');
            return;
        }

        // Max 3 minutes (check duration via video element)
        const videoEl = document.createElement('video');
        videoEl.preload = 'metadata';
        videoEl.onloadedmetadata = () => {
            URL.revokeObjectURL(videoEl.src);
            if (videoEl.duration > 180) {
                setError(t('upload.errors.videoTooLong') || 'Video must be less than 3 minutes');
                return;
            }

            setVideo({
                file,
                preview: URL.createObjectURL(file),
                duration: Math.floor(videoEl.duration),
                name: file.name,
            });
            setError('');
        };
        videoEl.src = URL.createObjectURL(file);
    };

    const removeVideo = () => {
        if (video) {
            URL.revokeObjectURL(video.preview);
            setVideo(null);
            setVideoProgress(0);
        }
    };

    const updateConfig = (category, value) => {
        setSelectedConfig(prev => {
            if (category === 'accessories') {
                const current = prev.accessories || [];
                const exists = current.includes(value);
                return {
                    ...prev,
                    accessories: exists
                        ? current.filter(id => id !== value)
                        : [...current, value],
                };
            }
            return { ...prev, [category]: value };
        });
    };

    const useCurrentConfig = () => {
        if (exportConfig) {
            setSelectedConfig(exportConfig());
        } else {
            setSelectedConfig(currentConfig);
        }
    };

    const handleSubmit = async () => {
        setError('');

        if (images.length === 0) {
            setError(t('upload.errors.noImages') || 'Please upload at least one image');
            return;
        }

        if (!formData.title.trim()) {
            setError(t('upload.errors.noTitle') || 'Please enter a title');
            return;
        }

        setSubmitting(true);

        try {
            // 1. Create configuration
            const createResult = await api.createConfiguration({
                title: formData.title.trim(),
                description: formData.description.trim(),
                config_data: selectedConfig,
                total_price: totalPrice,
            });

            if (!createResult.success) {
                throw new Error(createResult.error || 'Failed to create configuration');
            }

            const configId = createResult.data.id;

            // 2. Upload images
            const imageFiles = images.map(img => img.file);
            const uploadResult = await api.uploadConfigurationImages(configId, imageFiles);

            if (!uploadResult.success) {
                console.warn('Image upload failed:', uploadResult.error);
            }

            // 3. Upload video if present
            if (video) {
                setUploading(true);
                try {
                    await api.uploadConfigurationVideo(configId, video.file, (progress) => {
                        setVideoProgress(progress);
                    });
                } catch (videoErr) {
                    console.warn('Video upload failed:', videoErr);
                } finally {
                    setUploading(false);
                }
            }

            // 4. Submit for review
            const submitResult = await api.submitConfiguration(configId);

            // 5. 保存配置信息，用于铸造传奇
            setUploadedConfigId(configId);
            setUploadedCoverImage(images[0]?.preview || null);

            // 6. 显示铸造传奇弹窗
            setShowForgeLegend(true);

        } catch (err) {
            console.error('Submit error:', err);
            setError(err.message || 'Failed to submit configuration');
        } finally {
            setSubmitting(false);
        }
    };

    // 铸造传奇成功回调
    const handleForgeLegendSuccess = (legend) => {
        console.log('Legend forged:', legend);
        // 用户可以选择查看传奇或继续
    };

    // 关闭铸造传奇弹窗
    const handleForgeLegendClose = () => {
        setShowForgeLegend(false);
        // 跳转到个人主页
        navigate('/profile', { state: { message: t('upload.submitSuccess') || 'Configuration submitted successfully!' } });
    };

    return (
        <div className={styles.upload}>
            <h1 className={styles.title}>{t('upload.title') || 'Upload Your Configuration'}</h1>

            {/* Progress Steps */}
            <div className={styles.steps}>
                <div className={`${styles.step} ${step >= 1 ? styles.active : ''}`}>
                    <span className={styles.stepNumber}>1</span>
                    <span>{t('upload.step1') || 'Images'}</span>
                </div>
                <div className={styles.stepLine}></div>
                <div className={`${styles.step} ${step >= 2 ? styles.active : ''}`}>
                    <span className={styles.stepNumber}>2</span>
                    <span>{t('upload.step2') || 'Configuration'}</span>
                </div>
                <div className={styles.stepLine}></div>
                <div className={`${styles.step} ${step >= 3 ? styles.active : ''}`}>
                    <span className={styles.stepNumber}>3</span>
                    <span>{t('upload.step3') || 'Details'}</span>
                </div>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            {/* Step 1: Images */}
            {step === 1 && (
                <div className={styles.stepContent}>
                    <h2>{t('upload.uploadImages') || 'Upload Images (1-10)'}</h2>
                    <p className={styles.hint}>
                        {t('upload.imagesHint') || 'Upload photos of your real motorcycle. First image will be the cover.'}
                    </p>

                    <div className={styles.imageGrid}>
                        {images.map((img, index) => (
                            <div key={img.id} className={styles.imageCard}>
                                <img src={img.preview} alt="" />
                                {index === 0 && <span className={styles.coverBadge}>Cover</span>}
                                <div className={styles.imageActions}>
                                    <button onClick={() => moveImage(img.id, -1)} disabled={index === 0}>↑</button>
                                    <button onClick={() => moveImage(img.id, 1)} disabled={index === images.length - 1}>↓</button>
                                    <button onClick={() => removeImage(img.id)} className={styles.removeBtn}>×</button>
                                </div>
                            </div>
                        ))}

                        {images.length < 10 && (
                            <button
                                className={styles.addImageBtn}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <span>+</span>
                                <span>{t('upload.addImage') || 'Add Image'}</span>
                            </button>
                        )}
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />

                    {/* Video Upload Section */}
                    <div className={styles.videoSection}>
                        <h3>{t('upload.videoTitle') || 'Video (Optional)'}</h3>
                        <p className={styles.videoHint}>
                            {t('upload.videoHint') || 'Add a short video to showcase your motorcycle (max 3 minutes, 500MB)'}
                        </p>

                        {video ? (
                            <div className={styles.videoPreview}>
                                <video src={video.preview} controls />
                                <div className={styles.videoInfo}>
                                    <span className={styles.videoName}>{video.name}</span>
                                    <span className={styles.videoDuration}>
                                        {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                                    </span>
                                    <button
                                        className={styles.removeVideoBtn}
                                        onClick={removeVideo}
                                    >
                                        {t('common.remove') || 'Remove'}
                                    </button>
                                </div>
                                {uploading && (
                                    <div className={styles.uploadProgress}>
                                        <div
                                            className={styles.progressBar}
                                            style={{ width: `${videoProgress}%` }}
                                        />
                                        <span>{videoProgress}%</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button
                                className={styles.addVideoBtn}
                                onClick={() => videoInputRef.current?.click()}
                            >
                                <span>🎬</span>
                                <span>{t('upload.addVideo') || 'Add Video'}</span>
                            </button>
                        )}

                        <input
                            ref={videoInputRef}
                            type="file"
                            accept="video/*"
                            onChange={handleVideoSelect}
                            style={{ display: 'none' }}
                        />
                    </div>

                    <div className={styles.stepActions}>
                        <button
                            className={styles.nextBtn}
                            onClick={() => setStep(2)}
                            disabled={images.length === 0}
                        >
                            {t('common.next') || 'Next'}
                        </button>
                    </div>
                </div>
            )}

            {/* Step 2: Configuration */}
            {step === 2 && (
                <div className={styles.stepContent}>
                    <h2>{t('upload.selectConfig') || 'Select Configuration'}</h2>
                    <p className={styles.hint}>
                        {t('upload.configHint') || 'Select the parts that match your motorcycle.'}
                    </p>

                    <button className={styles.useCurrentBtn} onClick={useCurrentConfig}>
                        {t('upload.useCurrentConfig') || 'Use Current Configurator Settings'}
                    </button>

                    <div className={styles.configPicker}>
                        {CATEGORIES.map(category => (
                            <div key={category.id} className={styles.categorySection}>
                                <h3>{t(category.nameKey) || category.id}</h3>
                                <div className={styles.partOptions}>
                                    {PARTS[category.id]?.map(part => {
                                        const isSelected = category.id === 'accessories'
                                            ? selectedConfig.accessories?.includes(part.id)
                                            : selectedConfig[category.id] === part.id;

                                        return (
                                            <button
                                                key={part.id}
                                                className={`${styles.partOption} ${isSelected ? styles.selected : ''}`}
                                                onClick={() => updateConfig(category.id, part.id)}
                                            >
                                                {part.colorCode && (
                                                    <span
                                                        className={styles.colorSwatch}
                                                        style={{ background: part.colorCode }}
                                                    />
                                                )}
                                                <span className={styles.partName}>
                                                    {t(part.nameKey) || part.id}
                                                </span>
                                                {part.price > 0 && (
                                                    <span className={styles.partPrice}>
                                                        +¥{part.price.toLocaleString()}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className={styles.totalSection}>
                        <span>{t('upload.totalPrice') || 'Total Price'}</span>
                        <span className={styles.totalPrice}>¥{totalPrice.toLocaleString()}</span>
                    </div>

                    <div className={styles.stepActions}>
                        <button className={styles.backBtn} onClick={() => setStep(1)}>
                            {t('common.back') || 'Back'}
                        </button>
                        <button className={styles.nextBtn} onClick={() => setStep(3)}>
                            {t('common.next') || 'Next'}
                        </button>
                    </div>
                </div>
            )}

            {/* Step 3: Details */}
            {step === 3 && (
                <div className={styles.stepContent}>
                    <h2>{t('upload.enterDetails') || 'Enter Details'}</h2>

                    <div className={styles.formGroup}>
                        <label>{t('upload.titleLabel') || 'Title'} *</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            placeholder={t('upload.titlePlaceholder') || 'Give your configuration a name'}
                            maxLength={100}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>{t('upload.descriptionLabel') || 'Description'}</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            placeholder={t('upload.descriptionPlaceholder') || 'Tell us about your build...'}
                            rows={4}
                            maxLength={2000}
                        />
                    </div>

                    {/* Preview */}
                    <div className={styles.preview}>
                        <h3>{t('upload.preview') || 'Preview'}</h3>
                        <div className={styles.previewCard}>
                            <div className={styles.previewImage}>
                                {images[0] && <img src={images[0].preview} alt="" />}
                            </div>
                            <div className={styles.previewInfo}>
                                <h4>{formData.title || 'Untitled'}</h4>
                                <p>{images.length} {t('upload.images') || 'images'}</p>
                                <p className={styles.previewPrice}>¥{totalPrice.toLocaleString()}</p>
                            </div>
                        </div>
                    </div>

                    <div className={styles.reviewNotice}>
                        <p>
                            {t('upload.reviewNotice') || 'Your submission will be reviewed within 3 working days. You will be notified once it is approved.'}
                        </p>
                    </div>

                    <div className={styles.stepActions}>
                        <button className={styles.backBtn} onClick={() => setStep(2)}>
                            {t('common.back') || 'Back'}
                        </button>
                        <button
                            className={styles.submitBtn}
                            onClick={handleSubmit}
                            disabled={submitting || !formData.title.trim()}
                        >
                            {submitting
                                ? (t('common.submitting') || 'Submitting...')
                                : (t('upload.submit') || 'Submit for Review')
                            }
                        </button>
                    </div>
                </div>
            )}

            {/* 铸造传奇弹窗 */}
            <ForgeLegendModal
                isOpen={showForgeLegend}
                onClose={handleForgeLegendClose}
                configurationId={uploadedConfigId}
                configurationTitle={formData.title}
                configurationImage={uploadedCoverImage}
                onSuccess={handleForgeLegendSuccess}
            />
        </div>
    );
}
